import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import ClaimJobPaymentEmailTemplate from 'models/core/claimJobPaymentEmailTemplate/Model.ClaimJobPaymentEmailTemplate';
import claimJobPaymentEmailTemplateDeleteApi from 'models/core/claimJobPaymentEmailTemplate/delete/Api.ClaimJobPaymentEmailTemplate.Delete';
import claimJobPaymentEmailTemplateSaveApi from 'models/core/claimJobPaymentEmailTemplate/save/Api.ClaimJobPaymentEmailTemplate.Save';
import ClaimJobPaymentStatusTriggers from 'models/enums/ClaimJobPaymentStatusTriggers';
import { useMemo } from 'react';
import { claimJobPaymentEmailTemplateIndexRoute } from './ClaimJobPaymentEmailTemplate.Index';

export enum ClaimJobPaymentEmailTemplateAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyClaimJobPaymentEmailTemplateAction = Utils.VerifyExtends<
  Module.Claims.ClaimJobPaymentEmailTemplate.Actions,
  Utils.ValueOf<typeof ClaimJobPaymentEmailTemplateAction>
>;
export default function useClaimJobPaymentEmailTemplate() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.Claims.ClaimJobPaymentEmailTemplate.Params,
    Module.Claims.ClaimJobPaymentEmailTemplate.Query
  >({
    route: claimJobPaymentEmailTemplateIndexRoute,
  });

  const canView =
    permissions.CREATE_EMAILTEMPLATE || permissions.EDIT_EMAILTEMPLATE || permissions.DELETE_EMAILTEMPLATE;
  const templateApi = useApi({ action: ClaimJobPaymentEmailTemplate.list });
  const listApi = useApi({
    action: ClaimJobPaymentEmailTemplate.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => {
      templateApi.execute((b) => b);
      pageRouter.redirect({}, { ...pageRouter.query, list: req });
    },
    wait: !canView,
  });

  const deleteApi = useApi({
    action: claimJobPaymentEmailTemplateDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: claimJobPaymentEmailTemplateSaveApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const availableStatuses = useMemo(() => {
    const usedStatuses = templateApi?.payload?.data?.map((it) => it.claimJobPaymentStatusTrigger) || [];
    const statuses = Object.values({ ...ClaimJobPaymentStatusTriggers })?.map((status) => status);

    return [
      ...statuses.filter((status: any) => !usedStatuses.includes(status)),
      ...[selected?.claimJobPaymentStatusTrigger],
    ];
  }, [templateApi?.payload?.data, selected]);

  return {
    canView,
    pageRouter,
    listApi,
    saveApi,
    deleteApi,
    selected,
    permissions,
    availableStatuses,
    views: {
      [ClaimJobPaymentEmailTemplateAction.DELETE]: !!(
        pageRouter.params.action === ClaimJobPaymentEmailTemplateAction.DELETE &&
        selected &&
        permissions.DELETE_EMAILTEMPLATE
      ),
      [ClaimJobPaymentEmailTemplateAction.EDIT]: !!(
        pageRouter.params.action === ClaimJobPaymentEmailTemplateAction.EDIT &&
        ((permissions.CREATE_EMAILTEMPLATE && !selected) || (permissions.EDIT_EMAILTEMPLATE && selected))
      ),
    },
  };
}

export function useClaimJobPaymentEmailTemplateProvider() {
  return useDataProvider<ReturnType<typeof useClaimJobPaymentEmailTemplate>>();
}
